import Promise from 'bluebird';
import { IpAuthenticator, AdobePassAuthenticator } from 'watch-authenticators';
import { AdobePass } from 'enums';
import * as untimelyLogoutTracker from 'lib/metrics/untimelyLogoutTracker';
import { ClientAPI } from 'lib/api';
import { queryString } from 'lib/format';
import {
    trackMvpdPicker,
    trackProviderSelect,
    trackAuthenticationComplete,
    trackAuthorizationStart,
    trackAuthorizationComplete,
    clearTrackingData
} from 'lib/omniture';
import {
    adobePassInitializedSelector,
    adobePassAuthenticatedSelector,
    userAuthSelector
} from 'selectors/authenticationSelectors';
import playingContentSelector from 'selectors/playingContentSelector';
import appSelector, { countrySelector } from 'selectors/appSelector';
import { hideNavSelector } from 'selectors/pageSelector';
import providerSelector from 'selectors/providerSelector';
import uniq from 'lodash/uniq';
import { contentSelect, contentUnSelect } from './appActions';

function getAdobePassRedirectUrl(content, hideNav) {
    if (content === null) {
        return '';
    }
    const { bucketId, groupId, id, network } = content;
    const qs = queryString({
        bucketId,
        groupId,
        id,
        network,
        hideNav
    });

    // if entering via SEO friendly parameter URL, chop all the path params off and use straight query params for redirectUrl
    const pathname = content.isHero ? global.location.pathname.replace(/\/_\/?.+/, '') : '/watch/player';
    const port = (global.location.port !== '' ? `:${global.location.port}` : '');
    return `${global.location.protocol}//${global.location.hostname}${port}${pathname}${qs}`;
}

// Reduxified Adobe Pass Actions
export const START_ADOBEPASS_INITIALIZE = 'START_ADOBEPASS_INITIALIZE';
export const COMPLETE_ADOBEPASS_INITIALIZE = 'COMPLETE_ADOBEPASS_INITIALIZE';
export const START_CHECK_AUTHENTICATION = 'START_CHECK_AUTHENTICATION';
export const COMPLETE_CHECK_AUTHENTICATION = 'COMPLETE_CHECK_AUTHENTICATION';
export const START_AUTHENTICATION = 'START_AUTHENTICATION';
export const COMPLETE_AUTHENTICATION = 'COMPLETE_AUTHENTICATION';
export const START_AUTHORIZATION = 'START_AUTHORIZATION';
export const COMPLETE_AUTHORIZATION = 'COMPLETE_AUTHORIZATION';
export const REQUEST_PROVIDERS = 'REQUEST_PROVIDERS';
export const RECEIVE_PROVIDERS = 'RECEIVE_PROVIDERS';
export const DISPLAY_PROVIDER_DIALOG = 'DISPLAY_PROVIDER_DIALOG';
export const CLOSE_PROVIDER_DIALOG = 'CLOSE_PROVIDER_DIALOG';
export const SELECT_PROVIDER = 'SELECT_PROVIDER';
export const START_LOGOUT = 'START_LOGOUT';
export const COMPLETE_LOGOUT = 'COMPLETE_LOGOUT';
export const GET_AUTHENTICATION_INFO = 'GET_AUTHENTICATION_INFO';
export const GET_METADATA = 'GET_METADATA';
export const COMPLETE_PREAUTH_CHECK = 'COMPLETE_PREAUTH_CHECK';
export const GET_LOCATION = 'GET_LOCATION';

// Reduxified IP actionsexport
export const START_CHECK_IP_AUTHENTICATION = 'START_CHECK_IP_AUTHENTICATION';
export const COMPLETE_CHECK_IP_AUTHENTICATION = 'COMPLETE_CHECK_IP_AUTHENTICATION';
export const GET_IP_AUTHENTICATION_INFO = 'GET_IP_AUTHENTICATION_INFO';

let ipAuthenticator = null;
let adobePassAuthenticator = null;

// TODO: Do this better
// TODO: replace with `canUseDOM`
if (process.browser) {
    ipAuthenticator = new IpAuthenticator({ partner: 'watchespn', platform: 'web' });
    adobePassAuthenticator = new AdobePassAuthenticator({
        elementId: 'ae_container',
        environment: AdobePassAuthenticator.Environment.PRODUCTION,
        refreshless: true,
        redirectUrl: window.location.href,
        accessEnableImpl: 'js',
        displayProviderDialogImpl: () => {}
    });
}

// define ip authentication actions
export function createStartCheckIpAuthenticationAction() {
    return {
        type: START_CHECK_IP_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function createCompleteCheckIpAuthenticationAction(isAuthenticated) {
    return {
        type: COMPLETE_CHECK_IP_AUTHENTICATION,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        authType: 'isp',
        startTime: Date.now()
    };
}

export function createGetIpAuthenticationInfoAction(info) {
    return {
        type: GET_IP_AUTHENTICATION_INFO,
        info
    };
}

export function createGetLocationAction(location) {
    return {
        type: GET_LOCATION,
        location
    };
}

export function createGetLocationFromUserDataAction() {
    return (dispatch) => {
        ipAuthenticator.getLocation().then((location) => {
            dispatch(createGetLocationAction(location));
        });
    };
}

export function createSmartIpAuthenticationAction() {
    return (dispatch, getState) => {
        if (ipAuthenticator !== null) {
            dispatch(createGetLocationFromUserDataAction());
            dispatch(createStartCheckIpAuthenticationAction());
            ipAuthenticator.checkAuthentication().then(isAuthenticated => {
                dispatch(createCompleteCheckIpAuthenticationAction(isAuthenticated));
                if (isAuthenticated === true) {
                    ipAuthenticator.getAuthenticationInfo().then(ipInfo => {
                        ClientAPI.getProviders({ country: countrySelector(getState()) }).then((providersFromApi) => {
                            const providerInfo = providersFromApi.filter(provider =>
                                (provider.ispId === ipInfo.mvpd)
                            ).shift();
                            dispatch(createGetIpAuthenticationInfoAction(providerInfo));
                        });
                    });
                }
            }, () => {
                dispatch(createCompleteCheckIpAuthenticationAction(false));
            });
        }
    };
}

// Define Adobe Pass Authentication Actions
export function createStartAdobePassInitializeAction() {
    return {
        type: START_ADOBEPASS_INITIALIZE,
        startTime: Date.now()
    };
}

export function createCompleteAdobePassInitializeAction() {
    return {
        type: COMPLETE_ADOBEPASS_INITIALIZE,
        completeTime: Date.now()
    };
}

// Define Adobe Pass Check Authentication Actions
export function createStartCheckAuthenticationAction() {
    return {
        type: START_CHECK_AUTHENTICATION,
        startTime: Date.now()
    };
}

export function createCompletePreauthCheckAction(adobeId, preauthorized) {
    return {
        type: COMPLETE_PREAUTH_CHECK,
        adobeId,
        preauthorized
    };
}

export function checkPreauthResources(provider) {
    return (dispatch, getState) => {
        const channels = getState().schedule.channels || [];
        const adobeIds = uniq(channels.map((channel) => (channel.adobeResource)).filter(adobeResource => !!adobeResource));
        adobePassAuthenticator.on(AdobePassAuthenticator.EventType.PREAUTHORIZED,
            (channel) => {
                dispatch(createCompletePreauthCheckAction(channel, true));
            });
        adobePassAuthenticator.on(AdobePassAuthenticator.EventType.NOT_PREAUTHORIZED,
            (channel) => {
                dispatch(createCompletePreauthCheckAction(channel, false));
            });
        if (ClientAPI.FULL_PREAUTH_PROVIDERS.indexOf(provider) >= 0) {
            adobePassAuthenticator.checkPreauthorizedResources(...adobeIds).then();
        }
    };
}

export function createCompleteCheckAuthenticationAction(isAuthenticated) {
    return {
        type: COMPLETE_CHECK_AUTHENTICATION,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        isAuthenticated,
        authType: 'mvpd',
        completeTime: Date.now()
    };
}

export function createStartAuthenticationAction(content) {
    return {
        type: START_AUTHENTICATION,
        content,
        startTime: Date.now()
    };
}

export function createCompleteAuthenticationAction(isAuthenticated, mvpd) {
    return {
        type: COMPLETE_AUTHENTICATION,
        directive: (isAuthenticated === true ? 'add' : 'remove'),
        isAuthenticated,
        authType: 'mvpd',
        mvpd,
        completeTime: Date.now()
    };
}

export function createStartAuthorizationAction(resourceId) {
    return {
        type: START_AUTHORIZATION,
        resourceId,
        startTime: Date.now()
    };
}

export function createCompleteAuthorizationAction(resourceId, token) {
    return {
        type: COMPLETE_AUTHORIZATION,
        resourceId,
        token,
        completeTime: Date.now()
    };
}

export function createRequestProvidersAction() {
    return {
        type: REQUEST_PROVIDERS,
        startTime: Date.now()
    };
}

export function createReceiveProvidersAction(providers) {
    return {
        type: RECEIVE_PROVIDERS,
        providers,
        completeTime: Date.now()
    };
}

// Provider Display and Select Action creators.
// The Display Provider Dialog action, which is triggered by startAuthentication
export function createDisplayProviderDialogAction() {
    return (dispatch) => {
        trackMvpdPicker();
        dispatch({
            type: DISPLAY_PROVIDER_DIALOG,
            showProviderDialog: true
        });
    };
}

export function createCloseProviderDialogAction() {
    return (dispatch) => {
        dispatch({
            type: CLOSE_PROVIDER_DIALOG,
            showProviderDialog: false
        });
        dispatch(contentUnSelect());
    };
}

export function createStartLogoutAction() {
    return {
        type: START_LOGOUT,
        startTime: Date.now()
    };
}

export function createCompleteLogoutAction() {
    return {
        type: COMPLETE_LOGOUT,
        directive: 'remove',
        isAuthenticated: false,
        authType: 'mvpd',
        completeTime: Date.now()
    };
}

export function createAndPerformLogoutAction(content) {
    return (dispatch, getState) => {
        dispatch(createStartLogoutAction());
        return adobePassAuthenticator.deauthenticate().then((isAuthenticated) => {
            // purposefully logged out, forget their authn ttl so we don't track a discrepancy
            untimelyLogoutTracker.reset();
            const playingContent = playingContentSelector(getState());
            if (isAuthenticated === false) {
                dispatch(createCompleteLogoutAction());
                if (content && content.authTypes) {
                    dispatch(contentUnSelect());
                    dispatch(contentSelect(content));
                }
            }
            return Promise.resolve(playingContent);
        });
    };
}

export function createGetAuthenticationInfoAction(info) {
    return (dispatch, getState) => {
        trackAuthenticationComplete({ userAuth: userAuthSelector(getState()), provider: info });
        dispatch({
            type: GET_AUTHENTICATION_INFO,
            info
        });
    };
}

export function createGetMetadataAction(key, value) {
    return {
        type: GET_METADATA,
        key,
        value
    };
}

export function initializeAdobePassAuthenticator() {
    return (dispatch) => {
        if (!adobePassAuthenticator.initialize) {
            return Promise.resolve(false);
        }
        dispatch(createStartAdobePassInitializeAction());
        return adobePassAuthenticator.initialize().then((initialized) => {
            if (initialized) {
                dispatch(createCompleteAdobePassInitializeAction());
            }
            return initialized;
        });
    };
}

export function checkAdobePassAuthentication() {
    return (dispatch, getState) => {
        const adobePassInitialized = adobePassInitializedSelector(getState());
        if (!adobePassInitialized) {
            return Promise.resolve(false);
        }
        dispatch(createStartCheckAuthenticationAction());
        return adobePassAuthenticator.checkAuthentication()
            .then((isAuthenticated) => {
                // let redux know we've completed authentication
                dispatch(createCompleteCheckAuthenticationAction(isAuthenticated));
            });
    };
}

export function getAdobePassAuthenticationInfo() {
    return adobePassAuthenticator.getAuthenticationInfo();
}

export function getAdobePassMetadata(key) {
    return adobePassAuthenticator.getMetadata(key);
}

export function getProviders(config) {
    return (dispatch) => {
        dispatch(createRequestProvidersAction());
        return ClientAPI.getProviders(config).then((providersFromApi) => {
            dispatch(createReceiveProvidersAction(providersFromApi));
        });
    };
}

export function createAsyncGetMetadataAction(key) {
    return (dispatch) => getAdobePassMetadata(key)
        .then((value = `${Math.random()}`) => {
            dispatch(createGetMetadataAction(key, value));
            return Promise.resolve(value);
        }, (error) => Promise.reject(error));
}

/*
 Start "Thunks"
 These are action creators that return a function which takes in a "dispatch" function as its parameter (used for Async)
 */

// Define Smart Adobe Pass Initialization Action
// Initializes Adobe, checks authentication, then gets provider list if not authenticated.
// Called on page load
export function createSmartAdobePassInitializeAction() {
    return (dispatch, getState) => {
        dispatch(initializeAdobePassAuthenticator()).then(() => {
            // after we're initialized, fire off Redux action and check authentication status
            dispatch(checkAdobePassAuthentication()).then(() => {
                // get provider information
                dispatch(getProviders({ country: countrySelector(getState()) })).then(() => {
                    const isAuthenticated = adobePassAuthenticatedSelector(getState());
                    const { providerDialog: { providers: providersFromApi } } = getState();
                    if (isAuthenticated) {
                        getAdobePassAuthenticationInfo().then(info => {
                            const providerInfo = providersFromApi.filter(({ abbreviation }) =>
                                abbreviation === info.mvpd
                            ).shift();

                            dispatch(createGetAuthenticationInfoAction(providerInfo));

                            // save the authn ttl to local storage so we can track a discrepancy later
                            if (!untimelyLogoutTracker.isAuthNTtlSaved()) {
                                untimelyLogoutTracker.saveInitialAuthNData(providerInfo);
                                getAdobePassMetadata(AdobePass.METADATA.TTL_AUTHN).then((ttl) => {
                                    untimelyLogoutTracker.saveAuthNTtl(ttl);
                                });
                            }

                            const { location: { path = '' } } = appSelector(getState());
                            // only check preAuth for schedule page
                            if (path.indexOf('schedule') !== -1) {
                                dispatch(checkPreauthResources(providerInfo.abbreviation));
                            }
                        });
                    } else {
                        untimelyLogoutTracker.trackUntimelyLogout();
                    }
                });
            });
        });
    };
}

export function createAndPerformStartAuthenticationAction(content) {
    return (dispatch, getState) => {
        const redirectUrl = getAdobePassRedirectUrl(content, hideNavSelector(getState())) || '';
        dispatch(createStartAuthenticationAction(content));
        return adobePassAuthenticator.authenticate(null, null, redirectUrl || '')
            .then((isAuthenticated) => {
                dispatch(createCompleteAuthenticationAction(isAuthenticated));
                return Promise.resolve(isAuthenticated);
            });
    };
}

export function createAndPerformStartAuthorizationAction(resource) {
    return (dispatch, getState) => {
        dispatch(createStartAuthorizationAction(resource));
        const state = getState();
        trackAuthorizationStart({
            userAuth: userAuthSelector(state),
            provider: providerSelector(state)
        });
        return adobePassAuthenticator.authorize(resource)
            .then((response) => {
                dispatch(createCompleteAuthorizationAction(response.resourceId, response.token));
                trackAuthorizationComplete({
                    userAuth: userAuthSelector(state),
                    provider: providerSelector(state)
                });
                if (!response.errorCode && !response.errorDetails) {
                    return Promise.resolve({
                        resourceId: response.resourceId,
                        token: response.token
                    });
                }
                return Promise.reject(new Error(response.errorDetails));
            })
            .catch(error => Promise.reject(error));
    };
}

export function createSelectProviderAction(provider) {
    return (dispatch, getState) => {
        const adobeProvider = {
            ID: (provider && provider.ID) || provider.abbreviation
        };

        if (adobeProvider.ID) {
            trackProviderSelect({ userAuth: userAuthSelector(getState()), provider });
        } else {
            clearTrackingData();
        }
        adobePassAuthenticator.selectProvider(adobeProvider).then((isAuthenticated) => {
            // this probably won't ever get called
            dispatch(createCompleteAuthenticationAction(isAuthenticated, adobeProvider.ID));
        });
    };
}



// WEBPACK FOOTER //
// ./app/common/actions/authenticationActions.js